import React, { useState } from 'react';
import { Mail, MessageSquare, Building, User, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [consultForm, setConsultForm] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (subscribeEmail) {
      const subscribers = JSON.parse(localStorage.getItem('subscribers') || '[]');
      if (!subscribers.includes(subscribeEmail)) {
        subscribers.push(subscribeEmail);
        localStorage.setItem('subscribers', JSON.stringify(subscribers));
        toast({
          title: 'Successfully subscribed!',
          description: 'You\'ll receive our bi-weekly reports straight to your inbox.'
        });
        setSubscribeEmail('');
      } else {
        toast({
          title: 'Already subscribed',
          description: 'This email is already on our list.'
        });
      }
    }
  };

  const handleConsultSubmit = (e) => {
    e.preventDefault();
    const consultations = JSON.parse(localStorage.getItem('consultations') || '[]');
    const newConsultation = {
      ...consultForm,
      id: Date.now(),
      date: new Date().toISOString()
    };
    consultations.push(newConsultation);
    localStorage.setItem('consultations', JSON.stringify(consultations));
    
    toast({
      title: 'Consultation request received!',
      description: 'We\'ll get back to you within 24-48 hours.'
    });
    
    setConsultForm({
      name: '',
      email: '',
      organization: '',
      message: ''
    });
  };

  const handleConsultChange = (e) => {
    setConsultForm({
      ...consultForm,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-amber-50 py-12 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Get in Touch</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Subscribe to our reports or request a consultation for your marketing project or business
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Newsletter Subscription */}
          <Card className="border-2 border-gray-100 hover:border-[#A0704E] transition-all duration-300">
            <CardHeader>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-[#A0704E]" />
              </div>
              <CardTitle className="text-2xl">Subscribe to Reports</CardTitle>
              <CardDescription className="text-base">
                Get bi-weekly industry insights delivered straight to your inbox. Free during our trial period.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubscribe} className="space-y-4">
                <div>
                  <Label htmlFor="subscribe-email" className="text-base">Email Address</Label>
                  <Input
                    id="subscribe-email"
                    type="email"
                    placeholder="you@example.com"
                    value={subscribeEmail}
                    onChange={(e) => setSubscribeEmail(e.target.value)}
                    required
                    className="mt-2 py-6 text-base border-2 border-gray-200 focus:border-[#A0704E]"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#A0704E] hover:bg-[#8B5F3E] text-white py-6 text-lg"
                >
                  Subscribe Free <Send className="ml-2 w-5 h-5" />
                </Button>
                <p className="text-sm text-gray-500 text-center">
                  No credit card required. Unsubscribe anytime.
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Consultation Request */}
          <Card className="border-2 border-gray-100 hover:border-[#A0704E] transition-all duration-300">
            <CardHeader>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-[#A0704E]" />
              </div>
              <CardTitle className="text-2xl">Request Consultation</CardTitle>
              <CardDescription className="text-base">
                Whether you're a student with a project or a business planning market entry, we're here to help.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleConsultSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-base">Name</Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Your full name"
                      value={consultForm.name}
                      onChange={handleConsultChange}
                      required
                      className="pl-10 py-6 text-base border-2 border-gray-200 focus:border-[#A0704E]"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-base">Email</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={consultForm.email}
                      onChange={handleConsultChange}
                      required
                      className="pl-10 py-6 text-base border-2 border-gray-200 focus:border-[#A0704E]"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="organization" className="text-base">University / Organization</Label>
                  <div className="relative mt-2">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="organization"
                      name="organization"
                      type="text"
                      placeholder="e.g., University of Melbourne or StartupCo"
                      value={consultForm.organization}
                      onChange={handleConsultChange}
                      required
                      className="pl-10 py-6 text-base border-2 border-gray-200 focus:border-[#A0704E]"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-base">Tell us about your project or needs</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Describe your marketing project, business goals, or what insights you're looking for..."
                    value={consultForm.message}
                    onChange={handleConsultChange}
                    required
                    rows={5}
                    className="mt-2 text-base border-2 border-gray-200 focus:border-[#A0704E] resize-none"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#A0704E] hover:bg-[#8B5F3E] text-white py-6 text-lg"
                >
                  Submit Request <Send className="ml-2 w-5 h-5" />
                </Button>
                <p className="text-sm text-gray-500 text-center">
                  We typically respond within 24-48 hours.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Who Should Contact Us */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">Who Should Reach Out?</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-gray-100">
              <CardContent className="pt-8">
                <h3 className="text-xl font-bold text-[#A0704E] mb-4">Students</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#A0704E] rounded-full mt-2 flex-shrink-0" />
                    <span>Working on IMC or marketing strategy projects</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#A0704E] rounded-full mt-2 flex-shrink-0" />
                    <span>Developing startup business plans or market entry strategies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#A0704E] rounded-full mt-2 flex-shrink-0" />
                    <span>Need Australian market insights for coursework</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100">
              <CardContent className="pt-8">
                <h3 className="text-xl font-bold text-[#A0704E] mb-4">Businesses</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#A0704E] rounded-full mt-2 flex-shrink-0" />
                    <span>Startups and SMEs planning Australian market entry</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#A0704E] rounded-full mt-2 flex-shrink-0" />
                    <span>Growth teams targeting new Australian user segments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#A0704E] rounded-full mt-2 flex-shrink-0" />
                    <span>Looking for fresh perspectives from student researchers</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;